(() => {
  'use strict';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observedGalleries = new WeakSet();
  const initializedVideos = new WeakSet();
  const initializedPreviews = new WeakSet();
  const visiblePreviews = new Set();
  const manuallyPaused = new WeakSet();
  const automaticPauses = new WeakSet();
  let pending = false;

  function visible(wrapper) {
    if (!wrapper || document.hidden || wrapper.closest('[hidden]')) return false;
    if (wrapper.matches('.hidden, [hidden]')) return false;
    if (wrapper.matches('.swiper-slide:not(.swiper-slide-active)')) return false;
    const rect = wrapper.getBoundingClientRect();
    const style = getComputedStyle(wrapper);
    return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' &&
      rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
  }

  function pause(video) {
    if (video.paused) return;
    automaticPauses.add(video);
    video.pause();
  }

  function play(video) {
    if (!video.paused) return;
    const attempt = video.play();
    if (attempt) attempt.catch(() => {});
  }

  function updateSound(video) {
    const button = video.closest('.media')?.querySelector('[data-hondo-sound]');
    if (!button) return;
    const silent = video.muted || video.volume === 0;
    const label = silent ? button.dataset.soundOn : button.dataset.soundOff;
    button.textContent = label;
    button.setAttribute('aria-label', label);
  }

  function syncPreviews() {
    for (const video of visiblePreviews) {
      if (!video.isConnected) { video.pause(); previewObserver?.unobserve(video); visiblePreviews.delete(video); continue; }
      if (document.hidden || reducedMotion.matches || !visible(video)) {
        video.pause();
        continue;
      }
      video.muted = true;
      if (!video.hasAttribute('src')) video.src = video.dataset.src;
      play(video);
    }
  }

  const previewObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
        visiblePreviews.add(entry.target);
      } else {
        visiblePreviews.delete(entry.target);
        entry.target.pause();
      }
    }
    syncPreviews();
  }, { threshold: [0, 0.25] }) : null;

  function sync() {
    pending = false;
    document.querySelectorAll('[data-product-media-wrapper] .media video').forEach(video => {
      const wrapper = video.closest('[data-product-media-wrapper]');
      if (!visible(wrapper)) { pause(video); return; }
      if (reducedMotion.matches || manuallyPaused.has(video)) return;
      if (video.closest('.media').dataset.autoplayVideo === 'true') play(video);
    });
    syncPreviews();
  }

  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(sync);
  }

  function initialize() {
    document.querySelectorAll('.product__media-container').forEach(gallery => {
      if (observedGalleries.has(gallery)) return;
      observedGalleries.add(gallery);
      new MutationObserver(records => {
        if (records.some(record => record.type === 'childList')) initialize();
        schedule();
      }).observe(gallery, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden'] });
    });
    document.querySelectorAll('[data-hondo-preview]').forEach(video => {
      if (initializedPreviews.has(video)) return;
      initializedPreviews.add(video);
      video.muted = true;
      video.addEventListener('playing', () => video.classList.add('is-ready'));
      previewObserver?.observe(video);
    });
    document.querySelectorAll('[data-product-media-wrapper] .media video').forEach(video => {
      if (initializedVideos.has(video)) return;
      initializedVideos.add(video);
      video.addEventListener('volumechange', () => updateSound(video));
      video.addEventListener('pause', () => {
        if (automaticPauses.has(video)) { automaticPauses.delete(video); return; }
        if (visible(video.closest('[data-product-media-wrapper]'))) manuallyPaused.add(video);
      });
      video.addEventListener('play', () => manuallyPaused.delete(video));
      updateSound(video);
    });
    schedule();
  }

  document.addEventListener('click', event => {
    const control = event.target.closest('[data-plyr="play"], .plyr__video-wrapper');
    const video = control?.closest('.media')?.querySelector('video');
    if (!video) return;
    if (video.paused) manuallyPaused.delete(video);
    else manuallyPaused.add(video);
  }, true);

  document.addEventListener('click', event => {
    const sound = event.target.closest('[data-hondo-sound]');
    if (sound) {
      const video = sound.closest('.media').querySelector('video');
      if (!video) return;
      video.muted = !(video.muted || video.volume === 0);
      if (!video.muted && video.volume === 0) video.volume = 1;
      updateSound(video);
      return;
    }
    const thumbnail = event.target.closest('[data-product-thumbnail]');
    if (!thumbnail || !thumbnail.querySelector('[data-hondo-preview]')) return;
    const gallery = thumbnail.closest('.product__media-container');
    const wrapper = Array.from(gallery.querySelectorAll('[data-product-media-wrapper]'))
      .find(item => item.dataset.mediaItemId === thumbnail.dataset.thumbnailId);
    const video = wrapper?.querySelector('.media video');
    if (!video) return;
    manuallyPaused.delete(video);
    video.muted = false;
    video.volume = 1;
    updateSound(video);
    const attempt = video.play();
    if (attempt) attempt.catch(() => { video.muted = true; updateSound(video); play(video); });
    schedule();
  });

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      document.querySelectorAll('[data-product-media-wrapper] .media video').forEach(pause);
      for (const video of visiblePreviews) video.pause();
    } else schedule();
  });
  reducedMotion.addEventListener('change', schedule);
  document.addEventListener('shopify:section:load', initialize);
  new MutationObserver(records => {
    const hasNewGallery = records.some(record => Array.from(record.addedNodes).some(node =>
      node.nodeType === 1 && (node.matches('.product__media-container, [data-hondo-preview]') ||
        node.querySelector('.product__media-container, [data-hondo-preview]'))
    ));
    if (hasNewGallery) initialize();
  }).observe(document.body, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
})();
